import { asString, useQuery } from "portal/utils/hooks/useQuery";
import { CUSTOMER_QUERY } from "portal/utils/routing";
import { doNothing } from "portal/utils/functions";
import { LOCALSTORAGE_CUSTOMER_SCOPE } from "portal/utils/localStorage";
import { NavigateOptions } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useSelf } from "portal/state/store";

/** Replace so hydrating `?customer=` from localStorage does not add history entries. */
const replaceCustomerQuery: NavigateOptions = { replace: true };

/** Persisted customer scope for admins; non-admins are locked to self. */
export const useCustomerScopeFilter = (
  localStorageKey: string = LOCALSTORAGE_CUSTOMER_SCOPE
): {
  canSeeOtherCustomers: boolean;
  customerUuid: string | undefined;
  setCustomerUuid: (customerUuid: string | undefined) => void;
} => {
  const canSeeOtherCustomers = useCanSeeOtherCustomers();
  const { user, customer } = useSelf();
  const selfCustomerUuid = customer?.uuid ?? user?.appMetadata?.customerUuid;

  const [storedCustomerUuid, setStoredCustomerUuid] = useLocalStorage<
    string | undefined
  >(localStorageKey, selfCustomerUuid);
  const [urlCustomerUuid, setUrlCustomerUuid] = useQuery(
    CUSTOMER_QUERY,
    asString,
    replaceCustomerQuery
  );

  const setCustomerUuid = useCallback(
    (customerUuid: string | undefined) => {
      setStoredCustomerUuid(customerUuid);
      setUrlCustomerUuid(customerUuid);
    },
    [setStoredCustomerUuid, setUrlCustomerUuid]
  );

  if (
    canSeeOtherCustomers &&
    urlCustomerUuid &&
    urlCustomerUuid !== storedCustomerUuid
  ) {
    setStoredCustomerUuid(urlCustomerUuid);
  }

  useEffect(() => {
    if (canSeeOtherCustomers && !urlCustomerUuid && storedCustomerUuid) {
      setUrlCustomerUuid(storedCustomerUuid);
    }
  }, [
    canSeeOtherCustomers,
    setUrlCustomerUuid,
    storedCustomerUuid,
    urlCustomerUuid,
  ]);

  if (!canSeeOtherCustomers) {
    return {
      canSeeOtherCustomers: false,
      customerUuid: selfCustomerUuid,
      setCustomerUuid: doNothing, // Non-admins are locked to their own customer.
    };
  }

  return {
    canSeeOtherCustomers: true,
    customerUuid: urlCustomerUuid ?? storedCustomerUuid,
    setCustomerUuid,
  };
};
